/**
 * Utility functions for color manipulation and extraction
 */
import { getColorSync, getPaletteSync } from 'colorthief';

export const extractVibrantColors = async (logoUrl: string) => {
  // Extract a larger palette to have more colors to choose from
  const palette = await extractColorPalette(logoUrl, 8);

  if (palette.length >= 3) {
    // Get the most vibrant colors from the palette
    const vibrantColors = getMostVibrantColors(palette, 3);

    // Use the vibrant colors for the gradient
    const primary = vibrantColors[0];
    const secondary = getLighterColor(vibrantColors[1], 0.4);
    // Use the third vibrant color if available, otherwise create a lighter version of primary
    const tertiary = vibrantColors[2] || getLighterColor(primary, 0.4);

    return {
      primary,
      secondary,
      tertiary,
    };
  }

  // Fall back to dominant color approach if needed
  const dominantColor = await extractDominantColor(logoUrl);

  // Generate complementary and lighter colors
  const complementaryColor = getComplementaryColor(dominantColor);
  const lighterColor = getLighterColor(dominantColor, 0.4);

  return {
    primary: dominantColor,
    secondary: complementaryColor,
    tertiary: lighterColor,
  };
};

/**
 * Extracts the dominant color from an image URL
 * @param imageUrl URL of the image to analyze
 * @returns Promise that resolves to the dominant color in hex format
 */
export const extractDominantColor = (imageUrl: string): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';

    img.onload = () => {
      try {
        // Use the ColorThief browser API
        const dominantColor = getColorSync(img);
        console.log('ColorThief extracted dominant color:', dominantColor);

        if (dominantColor) {
          console.log('ColorThief extracted dominant color:', dominantColor);
          const [r, g, b] = dominantColor.array();
          const hexColor = rgbToHex(r, g, b);
          resolve(hexColor);
          return;
        }

        // Fallback to our simple algorithm if ColorThief fails
        console.warn(
          'ColorThief failed to extract color, using fallback method',
        );

        // Create a canvas to draw the image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          console.error('Could not get canvas context');
          resolve(getFallbackColor());
          return;
        }

        // Set canvas dimensions to image dimensions
        canvas.width = img.width;
        canvas.height = img.height;

        // Draw image to canvas
        ctx.drawImage(img, 0, 0, img.width, img.height);

        try {
          // Get image data
          const imageData = ctx.getImageData(
            0,
            0,
            canvas.width,
            canvas.height,
          ).data;

          // Use simple color counting as fallback
          const simpleColor = getSimpleDominantColor(imageData);
          console.log('Simple algorithm extracted color:', simpleColor);

          resolve(simpleColor);
        } catch (error) {
          console.error('Error extracting color data:', error);
          resolve(getFallbackColor());
        }
      } catch (error) {
        console.error('Error in color extraction:', error);
        resolve(getFallbackColor());
      }
    };

    img.onerror = (error) => {
      console.error('Failed to load image:', error, imageUrl);
      resolve(getFallbackColor());
    };

    // Add a timeout to handle cases where the image might hang
    setTimeout(() => {
      if (!img.complete) {
        console.warn('Image load timed out:', imageUrl);
        resolve(getFallbackColor());
      }
    }, 5000);

    img.src = imageUrl;
  });
};

/**
 * Extracts a color palette from an image URL
 * @param imageUrl URL of the image to analyze
 * @param colorCount Number of colors to extract (default: 5)
 * @returns Promise that resolves to an array of hex colors
 */
export const extractColorPalette = (
  imageUrl: string,
  colorCount = 5,
): Promise<string[]> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';

    img.onload = () => {
      try {
        // Use the ColorThief browser API
        const palette = getPaletteSync(img, { colorCount });

        if (palette && palette.length > 0) {
          console.log('ColorThief extracted palette:', palette);
          const hexColors = palette.map((color) => color.hex());
          resolve(hexColors);
          return;
        }

        console.warn('ColorThief failed to extract palette, using fallback');
        resolve([getFallbackColor()]);
      } catch (error) {
        console.error('Error extracting color palette:', error);
        resolve([getFallbackColor()]);
      }
    };

    img.onerror = (error) => {
      console.error('Failed to load image for palette extraction:', error);
      resolve([getFallbackColor()]);
    };

    // Add a timeout to handle cases where the image might hang
    setTimeout(() => {
      if (!img.complete) {
        console.warn('Image load timed out for palette extraction:', imageUrl);
        resolve([getFallbackColor()]);
      }
    }, 5000);

    img.src = imageUrl;
  });
};

/**
 * Get a fallback color when extraction fails
 */
const getFallbackColor = (): string => {
  return '#ff00bf';
};

/**
 * Simple color counting algorithm
 */
const getSimpleDominantColor = (imageData: Uint8ClampedArray): string => {
  // Simple color counting approach
  const colorCounts: Record<string, number> = {};

  // Sample pixels (every 20th pixel to improve performance)
  for (let i = 0; i < imageData.length; i += 20) {
    const r = imageData[i];
    const g = imageData[i + 1];
    const b = imageData[i + 2];
    const a = imageData[i + 3];

    // Skip transparent pixels
    if (a < 128) continue;

    // Skip very light colors (likely background)
    if (r > 240 && g > 240 && b > 240) continue;

    // Skip very dark colors (likely text or outlines)
    if (r < 20 && g < 20 && b < 20) continue;

    // Quantize colors to reduce the number of unique colors
    const quantizedR = Math.round(r / 10) * 10;
    const quantizedG = Math.round(g / 10) * 10;
    const quantizedB = Math.round(b / 10) * 10;

    const colorKey = `${quantizedR},${quantizedG},${quantizedB}`;
    colorCounts[colorKey] = (colorCounts[colorKey] || 0) + 1;
  }

  // Find the most common color
  let maxCount = 0;
  let dominantColorKey = '0,0,0';

  for (const colorKey in colorCounts) {
    if (colorCounts[colorKey] > maxCount) {
      maxCount = colorCounts[colorKey];
      dominantColorKey = colorKey;
    }
  }

  // Convert back to RGB
  const [r, g, b] = dominantColorKey.split(',').map(Number);

  // Convert to hex
  return rgbToHex(r, g, b);
};

/**
 * Convert RGB values to hex color string
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (value: number) => {
    const hex = value.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Generates a complementary color for a given hex color
 * @param hexColor The base color in hex format
 * @returns Complementary color in hex format
 */
export const getComplementaryColor = (hexColor: string): string => {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Get complementary color (255 - value)
  const compR = 255 - r;
  const compG = 255 - g;
  const compB = 255 - b;

  // Convert back to hex
  return rgbToHex(compR, compG, compB);
};

/**
 * Generates a lighter version of a color
 * @param hexColor The base color in hex format
 * @param factor How much to lighten (0-1)
 * @returns Lightened color in hex format
 */
export const getLighterColor = (hexColor: string, factor = 0.3): string => {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Lighten
  const lighterR = Math.min(255, r + (255 - r) * factor);
  const lighterG = Math.min(255, g + (255 - g) * factor);
  const lighterB = Math.min(255, b + (255 - b) * factor);

  // Convert back to hex
  return rgbToHex(
    Math.round(lighterR),
    Math.round(lighterG),
    Math.round(lighterB),
  );
};

/**
 * Calculates color brightness (0-255)
 * Higher values indicate lighter colors
 */
export const getColorBrightness = (hexColor: string): number => {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate perceived brightness using the formula
  // (0.299*R + 0.587*G + 0.114*B)
  return Math.round(0.299 * r + 0.587 * g + 0.114 * b);
};

/**
 * Determines if a color is dark (needs light text)
 */
export const isColorDark = (hexColor: string): boolean => {
  return getColorBrightness(hexColor) < 128;
};

/**
 * Calculates the vibrancy/saturation of a color
 * Higher values indicate more vibrant colors
 * @param hexColor The color in hex format
 * @returns A number representing the vibrancy (0-1)
 */
export const getColorVibrancy = (hexColor: string): number => {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  // Convert RGB to HSV (Hue, Saturation, Value)
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  // Saturation is the primary measure of vibrancy
  const saturation = max === 0 ? 0 : delta / max;

  // Value (brightness) also contributes to perceived vibrancy
  const value = max;

  // Combine saturation and value for a vibrancy score
  // Weight saturation more heavily (0.7) than value (0.3)
  return saturation * 0.7 + value * 0.3;
};

/**
 * Sorts colors by vibrancy (most vibrant first)
 * @param colors Array of hex color strings
 * @returns Sorted array with most vibrant colors first
 */
export const sortColorsByVibrancy = (colors: string[]): string[] => {
  return [...colors].sort((a, b) => {
    const vibrancyA = getColorVibrancy(a);
    const vibrancyB = getColorVibrancy(b);
    return vibrancyB - vibrancyA; // Descending order (most vibrant first)
  });
};

/**
 * Gets the most vibrant colors from a palette
 * @param palette Array of hex color strings
 * @param count Number of colors to return
 * @returns Array of the most vibrant colors
 */
export const getMostVibrantColors = (
  palette: string[],
  count = 3,
): string[] => {
  const sortedColors = sortColorsByVibrancy(palette);
  return sortedColors.slice(0, Math.min(count, sortedColors.length));
};
